import { CheckIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/solid';
import { CellContext } from '@tanstack/react-table';
import { useState, useEffect } from 'react';
import AlertDialog from 'src/components/alert-dialog';
import { Button } from 'src/components/button';
import { CloseIon } from 'src/components/icons/CloseIcon';

export function EditCell<TData>({ row, table }: CellContext<TData, unknown>) {
  const { meta } = table.options;
  const [warning, setWarning] = useState('');
  const [validTooltip, setValidTooltip] = useState('');

  const setEditedRows = (elName: 'edit' | 'done' | 'cancel' | 'success') => {
    if (elName === 'done') {
      const isValidMessage = meta?.preSaveValidation?.();
      if (isValidMessage) {
        setValidTooltip(isValidMessage);
        return;
      }
      setValidTooltip('');

      const warningMessage = meta?.preSaveWarning?.();
      if (warningMessage) {
        setWarning(warningMessage);
        return;
      }
      setWarning('');
    }
    meta?.setEditedRows((old: Record<string, boolean>) => ({
      ...(old ?? {}),
      [row.id]: !old[row.id],
    }));
    if (elName !== 'edit') {
      meta?.revertData(row.index, elName === 'cancel');
    }
  };

  const removeRow = () => {
    meta?.removeRow(row.index);
  };

  useEffect(() => {
    const validRow = meta?.validRows[row.id];
    const disableSubmit = validRow
      ? Object.values(validRow)?.some((item) => !item)
      : false;
    setValidTooltip(disableSubmit ? `invalid data` : '');
  }, [meta?.validRows, row.id]);

  return (
    <div className="flex justify-center">
      {meta?.editedRows[row.id] ? (
        <div className="flex gap-2">
          <Button
            data-testid={`data-table-action-cell-cancel-button-${row.id}`}
            name="cancel"
            color="red"
            onClick={() => setEditedRows('cancel')}
          >
            <CloseIon className="h-4 w-4" />
          </Button>
          <Button
            name="done"
            data-testid={`data-table-action-cell-done-button-${row.id}`}
            data-tooltip-id="vlan-tooltip"
            data-tooltip-content={validTooltip}
            data-tooltip-place="top"
            color="green"
            disabled={!!validTooltip}
            onClick={(e) => {
              if (validTooltip) return;
              setEditedRows('done');
            }}
          >
            <CheckIcon className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <div className="flex gap-2">
          {meta?.enableRowEditing && (
            <Button
              data-testid={`data-table-action-cell-edit-button-${row.id}`}
              name="edit"
              className="border border-gray-900 bg-white text-gray-900 hover:bg-blue-400 hover:text-white data-[hover]:bg-blue-400"
              onClick={() => {
                setEditedRows('edit');
              }}
            >
              <PencilIcon className="h-4 w-4" />
            </Button>
          )}
          {meta?.enableRemoveRow && (
            <Button
              data-testid={`data-table-action-cell-remove-button-${row.id}`}
              name="remove"
              className="border border-red-500 bg-white text-red-500 hover:bg-red-400 hover:text-white data-[hover]:bg-red-400"
              onClick={removeRow}
            >
              <TrashIcon className="h-4 w-4" />
            </Button>
          )}
        </div>
      )}
      <AlertDialog
        dataTestId={`data-table-action-cell-warning-dialog-${row.id}`}
        variant="warning"
        title="Warning"
        description={warning}
        confirmLabel="Confirm"
        closeLabel="Cancel"
        open={!!warning}
        onClose={() => setWarning('')}
        onConfirm={() => {
          setEditedRows('success');
          setWarning('');
        }}
      />
    </div>
  );
}
